import React, { useState } from 'react';
import { Grid, Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';

import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Diversity1Icon from '@mui/icons-material/Diversity1';
import sampleImage from 'assets/img/sample-image.png';

import { notification } from "antd";

const useStyles = makeStyles((theme) => ({
  rootGeneral: {
    flexGrow: 1,
    margin: '1% 2%',
    [theme.breakpoints.down('md')]: {
      margin: '2% 1%',
    },
    [theme.breakpoints.down('sm')]: {
      margin: '1% 0.5%',
    },
  },
  root: {
    flexGrow: 1,
    margin: '0 2%',
    cursor: 'pointer',
    [theme.breakpoints.down('md')]: {
      margin: '0 1%',
    },
    [theme.breakpoints.down('sm')]: {
      margin: '0 0.5%',
    },
  },
  fieldset: {
    border: `4px solid ${theme.palette.primary.main}`,
    borderRadius: '50px',
    margin: '10px 20px',
    [theme.breakpoints.down('md')]: {
      margin: '10px 15px',
      borderRadius: '30px',
      border: '3px solid #26516F',
    },
    [theme.breakpoints.down('sm')]: {
      margin: '10px 5px',
      borderRadius: '20px',
      border: '2px solid #26516F',
    },
  },
  legend: {
    color: theme.palette.primary.main,
    fontSize: '2em',
    fontWeight: '700',
    marginLeft: '30px',
    padding: '0 10px',
    maxWidth: '160px',
    [theme.breakpoints.down('md')]: {
      fontSize: '1.5em',
      marginLeft: '20px',
      maxWidth: '140px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2em',
      marginLeft: '15px',
      maxWidth: '120px',
      padding: '0 5px',
    },
  },
  serviceCard: {
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.2)',
      [theme.breakpoints.up('md')]: {
        transform: 'scale(1.05)',
      },
    },
  },
  accordionRoot: {
    '&:before': {
      display: 'none',
    },
    marginBottom: theme.spacing(1),
    borderRadius: '8px !important',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
}));

const extractUserDataField = (source, fieldNames) => {
  for (const fieldName of fieldNames) {
    const match = source.match(new RegExp(`(?:${fieldName})["'=:\\s]*"?([A-Za-z0-9._-]*)`, 'i'));

    if (match) {
      return { found: true, value: match[1] || null };
    }
  }

  return { found: false, value: null };
};

const parseLandingUserData = (dynamicPart) => {
  if (!dynamicPart || !dynamicPart.startsWith('/?')) {
    return null;
  }

  const decodedSource = decodeURIComponent(dynamicPart.substring(2)).replace(/&quot;/g, '"');
  const userIdField = extractUserDataField(decodedSource, ['user_id', 'userId']);
  const qcIdField = extractUserDataField(decodedSource, ['qc_id', 'qcID', 'qcid', 'qcId']);

  if (!userIdField.found && !qcIdField.found) {
    return null;
  }

  return {
    user_id: userIdField.value,
    qcID: qcIdField.value
  };
};

export const buildApplicationFormUrl = (pathname, search = '') => {
  const currentUrl = `${pathname}${search || ''}`;
  const baseUrl = currentUrl.split("/kabahagi")[0] || '';
  const dynamicPart = currentUrl.split("/kabahagi")[1] || '';
  const parsedUserData = parseLandingUserData(dynamicPart);

  if (parsedUserData) {
    return `${baseUrl}/kabahagi/application-form/${encodeURIComponent(JSON.stringify(parsedUserData))}`;
  }

  return `${baseUrl}/kabahagi/application-form${dynamicPart}`;
};

const ResponsiveLandingPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [expanded, setExpanded] = useState('panel1');

  const handleChangeExpanded = (value) => {
    setExpanded(expanded === value ? false : value);
  };

  const handleShowNotif = () => {
    notification.error({
      message: "Application Disabled",
      description: "The online application is currently unavailable. Please check back later.",
      duration: 5,
    });
  };

  const applicationFormUrl = buildApplicationFormUrl(window.location.pathname, window.location.search);

  const accordionData = [
    {
      id: 'panel1',
      title: 'Disability Assessment And Diagnosis',
      content: 'Free check-ups from Specialized Doctors (Developmental Pediatrician and Rehabilitation Physician) to diagnose the children\'s condition based on age and development.'
    },
    {
      id: 'panel2',
      title: 'Inclusive Arts for Children with Disabilities',
      content: 'Free Art Class to develop children\'s skills and talent in art and boost their confidence in expressing their feelings in a creative way.'
    },
    {
      id: 'panel3',
      title: 'Livelihood Program For The Family Of Children With Disability',
      content: 'Free vocational skills training like dressmaking, hilot wellness, pastry making, bread making, investment capacity building, and other livelihood programs.'
    },
    {
      id: 'panel4',
      title: 'Free Group Training And Seminars For Families, Teacher Of The Children With Disability, Together With Other Disability-Inclusive Stakeholders In The City Like Barangay Council For The Protection Of Children',
      content: 'Therapists (Occupational Therapy, Physical Therapy, and Speech-Language Pathology) use various methods to train the parents or primary caregivers of children with disability. This is initiated with free individualized assessment, followed by training of parents on strategies for child handling in 10 sessions, along with the Child with Disability. This way, parents or primary caregivers can sustain the application of these strategies at home.'
    }
  ];

  return (
    <div maxWidth="xl" className={classes.rootGeneral}>
      <fieldset className={classes.fieldset}>
        <legend className={classes.legend}>Services</legend>
        <Box p={isMobile ? 1 : isTablet ? 1.5 : 2}>
          <Grid container spacing={isMobile ? 1 : 2}>
            {/* Application Card */}
            <Grid item xs={12} lg={4}>
              <Box className={classes.root} mb={isMobile ? 2 : 0}>
                <a href={applicationFormUrl} rel="noopener noreferrer" style={{ textDecoration: 'none' }} aria-label="Apply for free therapy services - opens application form">
                  <Box 
                    className={`frame hover-effect ${classes.serviceCard}`} 
                    mb={isMobile ? 2 : 4}
                    sx={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box 
                      className="card-head"
                      sx={{
                        background: 'linear-gradient(135deg, #26516F 0%, #1a3f5a 100%)',
                        minHeight: { xs: '60px', sm: '70px', md: '80px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: { xs: '10px', sm: '15px', md: '20px' },
                      }}
                    >
                      <Typography 
                        sx={{ 
                          fontSize: { 
                            xs: '14px', 
                            sm: '16px', 
                            md: '18px',
                            lg: '20px' 
                          }, 
                          fontWeight: 'bold',
                          color: 'white',
                          textAlign: 'center',
                          lineHeight: 1.2
                        }}
                      >
                        Click to Apply for a Free Therapy Service
                      </Typography>
                    </Box>
                    <Box 
                      className="card-body text-color" 
                      display="flex" 
                      justifyContent="center" 
                      alignItems="center" 
                      sx={{ 
                        height: { xs: "120px", sm: "140px", md: "160px", lg: "180px" },
                        backgroundColor: '#f8f9fa',
                        transition: 'background-color 0.3s ease',
                        '&:hover': {
                          backgroundColor: '#e9ecef',
                        }
                      }}
                    >
                      <Diversity1Icon 
                        sx={{ 
                          fontSize: { xs: 60, sm: 70, md: 80, lg: 100 },
                          color: '#26516F',
                          transition: 'transform 0.3s ease',
                        }} 
                      />
                    </Box>
                  </Box>
                </a>
              </Box>
            </Grid>
            
            {/* Information Section */}
            <Grid item xs={12} lg={8}>
              <Box sx={{ px: { xs: 0.5, sm: 1, md: 2 } }}>
                <Typography 
                  variant="body1" 
                  fontWeight="600" 
                  className="text-color" 
                  align="justify"
                  sx={{
                    fontSize: { xs: '14px', sm: '15px', md: '16px' },
                    lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                    mb: 3
                  }}
                >
                  Therapists (Occupational Therapy, Physical Therapy, and Speech-Language Pathology) use various methods to train the parents or primary caregivers
                  of children with disability. This is initiated with free individualized
                  assessment, followed by training of parents on strategies for child handling in 10 sessions, along with the Child with Disability. This way, parents or
                  primary caregivers can sustain the application of these strategies at home.
                </Typography>
                
                <Typography 
                  variant="h6" 
                  fontWeight="bold" 
                  className="text-color" 
                  mb={1}
                  sx={{ 
                    fontSize: { xs: '16px', sm: '18px', md: '20px' }
                  }}
                >
                  Who can apply?
                </Typography>
                
                <List component="ol" dense sx={{ py: 0, mb: 2 }}>
                  {['QCitizens', 'Children 0 to 17 years old', 'Those experiencing developmental issues'].map((item, index) => (
                    <ListItemText 
                      key={index}
                      primary={`${index + 1}. ${item}`} 
                      sx={{ 
                        '& .MuiListItemText-primary': { 
                          fontSize: { xs: '13px', sm: '14px', md: '15px' },
                          fontWeight: 500,
                          color: '#26516F',
                          marginBottom: '4px'
                        }
                      }}
                    />
                  ))}
                </List>
                
                <Typography 
                  variant="h6" 
                  fontWeight="bold" 
                  className="text-color" 
                  mb={1}
                  sx={{ 
                    fontSize: { xs: '16px', sm: '18px', md: '20px' }
                  }}
                >
                  What are the requirements?
                </Typography>
                
                <Grid container spacing={1} alignItems="flex-start">
                  <Grid item xs={isMobile ? 12 : 9}>
                    <List dense sx={{ py: 0 }}>
                      {[
                        'QCitizen ID of the Parent (required)',
                        '1pc 2x2 ID Photo of the Child (required)',
                        <span key="form">
                          Accomplished{' '}
                          <a 
                            href={applicationFormUrl} 
                            rel="noopener noreferrer" 
                            target="_blank"
                            style={{ 
                              color: '#26516F', 
                              textDecoration: 'underline',
                              fontWeight: 'bold'
                            }}
                          >
                            QC Kabahagi Center Application Form
                          </a>{' '}
                          (required)
                        </span>,
                        'QCitizen ID of the Child (optional)',
                        'Doctor\'s Assessment of the Child (at least 6 months) (optional)'
                      ].map((item, index) => (
                        <ListItemText 
                          key={index}
                          primary={typeof item === 'string' ? `${index + 1}. ${item}` : <span>{index + 1}. {item}</span>} 
                          sx={{ 
                            '& .MuiListItemText-primary': { 
                              fontSize: { xs: '13px', sm: '14px', md: '15px' },
                              fontWeight: 500,
                              color: '#26516F',
                              marginBottom: '4px'
                            }
                          }}
                        />
                      ))}
                    </List>
                  </Grid>
                  {!isMobile && (
                    <Grid item xs={3}>
                      <Box display="flex" justifyContent="center" mt={1}>
                        <img
                          src={sampleImage}
                          alt="Sample ID"
                          style={{ 
                            width: "80px", 
                            height: "auto", 
                            maxWidth: "100%",
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>
          </Grid>
          
          <Divider 
            variant="middle" 
            sx={{ 
              margin: { xs: '20px 0', sm: '30px 0', md: '40px 0' },
              borderColor: '#26516F',
              opacity: 0.3
            }} 
          />
          
          {/* Other Services Section */}
          <Box mb={isMobile ? 2 : 4}>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              fontWeight="bold" 
              className="text-color" 
              mb={3} 
              textAlign="center"
              sx={{
                fontSize: { xs: '20px', sm: '24px', md: '28px' }
              }}
            >
              Other Services We Offer:
            </Typography>
            
            {accordionData.map((accordion) => (
              <Accordion
                key={accordion.id}
                expanded={expanded === accordion.id}
                onChange={() => handleChangeExpanded(accordion.id)}
                className={classes.accordionRoot}
                sx={{
                  '&:before': { display: 'none' },
                  mb: 1,
                  borderRadius: '8px !important',
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon 
                    sx={{ 
                      color: expanded === accordion.id ? '#fff' : '#26516F',
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }} 
                  />}
                  sx={{
                    backgroundColor: expanded === accordion.id ? '#26516F' : '#f8f9fa',
                    color: expanded === accordion.id ? '#fff' : '#26516F',
                    minHeight: { xs: '56px', sm: '64px' },
                    '&.Mui-expanded': {
                      minHeight: { xs: '56px', sm: '64px' },
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: { xs: '8px 0', sm: '12px 0' },
                    },
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: expanded === accordion.id ? '#1a3f5a' : '#e9ecef',
                    }
                  }}
                >
                  <Typography 
                    fontWeight="bold"
                    sx={{
                      fontSize: { xs: '14px', sm: '16px', md: '18px' },
                      lineHeight: 1.3
                    }}
                  >
                    {accordion.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: '#fff',
                    padding: { xs: '16px', sm: '20px', md: '24px' },
                    borderTop: '1px solid #e0e0e0'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '13px', sm: '14px', md: '16px' },
                      lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
                      color: '#555',
                      textAlign: 'justify'
                    }}
                  >
                    {accordion.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      </fieldset>
    </div>
  );
};

export default ResponsiveLandingPage;
